import { useState, MouseEvent, useRef, useCallback } from 'react';
import { Box, IconButton, Popover } from '@mui/material';
import { SettingsIcon } from '#features/common/components/icons/SettingsIcon';
import { IntentName } from '#features/fdc3/model';
import { useIntentListener } from '#features/fdc3/useIntentListener';
import authService from '#services/auth';
import { ConfirmLogoutDialog } from './ConfirmLogoutDialog';
import { GlobalPreferencesPanel } from './GlobalPreferencesPanel';

export function SettingsMenuButton() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const iconButtonRef = useRef<HTMLButtonElement>(null);

  const onViewGlobalPreferences = useCallback(() => {
    setAnchorEl(iconButtonRef.current);
  }, []);

  useIntentListener(IntentName.ViewGlobalPreferences, onViewGlobalPreferences);

  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);

  const openSettingsMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeSettingsMenu = () => {
    setAnchorEl(null);
  };

  const confirmLogout = () => {
    closeLogoutConfirmation();
    authService.logout();
  };

  const closeLogoutConfirmation = () => {
    setLogoutConfirmationOpen(false);
  };

  const openLogoutConfirmation = () => {
    setLogoutConfirmationOpen(true);
  };

  return (
    <>
      <IconButton
        onClick={openSettingsMenu}
        data-testid="SettingsMenuButton"
        ref={iconButtonRef}
        sx={(theme) => ({
          color:
            theme.ifx.palette[
              'Universal + Components/Components/Buttons/Primary/button-primary-fg_subtle'
            ],
        })}
      >
        <SettingsIcon />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeSettingsMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Box
          sx={(theme) => ({
            border: `1px solid ${theme.ifx.palette['Colors/Border/border-primary']}`,
            width:
              theme.ifx.widths['width-4xl'] + theme.ifx.widths['width-md'] + 2,
          })}
        >
          <GlobalPreferencesPanel
            onLogout={() => {
              openLogoutConfirmation();
              closeSettingsMenu();
            }}
          />
        </Box>
      </Popover>
      <ConfirmLogoutDialog
        title="Sign Out"
        message="Are you sure you want to sign out from Instinct 2.0 ?"
        open={isLogoutConfirmationOpen}
        onCancel={closeLogoutConfirmation}
        onConfirm={confirmLogout}
      />
    </>
  );
}
